import { FeatureTableOptions } from 'types'
import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { parseString } from 'utils/parsingHelpers'
import { optionalNumberSchema, optionalStringSchema } from 'utils/zodHelpers'

import { GeckoWeld } from './geckoWelds'

export interface GeckoCircumferentialWeldJSON {
  name: string | undefined
  type: GeckoFeatureModelType.CircumferentialWeld
  feature_geometry: {
    y: number
  }
}
export const NewGeckoCircumferentialWeldJSON: GeckoCircumferentialWeldJSON = {
  name: 'New Circumferential Weld',
  type: GeckoFeatureModelType.CircumferentialWeld,
  feature_geometry: {
    y: 0,
  },
}

export class GeckoCircumferentialWeld extends GeckoWeld {
  public readonly type = GeckoFeatureModelType.CircumferentialWeld

  private constructor(
    componentID: string,
    weldID: string,
    weldJson: GeckoCircumferentialWeldJSON,
    componentGeometry?: GeckoComponentRect
  ) {
    const name = parseString(weldJson.name)
    const feature_geometry = weldJson.feature_geometry as Record<string, number>
    if (componentGeometry) {
      feature_geometry.x_start = componentGeometry.start.x.num
      feature_geometry.x_end = componentGeometry.end.x.num
    }
    super(
      componentID,
      weldID,
      name,
      feature_geometry.x_start as number,
      feature_geometry.y as number,
      feature_geometry.x_end as number,
      feature_geometry.y as number
    )
  }

  public static CreateMapFromJSON(
    component_id: string,
    recordsJson: Record<string, GeckoCircumferentialWeldJSON>,
    componentGeometry: GeckoComponentRect
  ): Map<string, GeckoCircumferentialWeld> {
    return Object.keys(recordsJson).reduce((resultObj, weldID) => {
      resultObj.set(
        weldID,
        new GeckoCircumferentialWeld(
          component_id,
          weldID,
          recordsJson[weldID],
          componentGeometry
        )
      )
      return resultObj
    }, new Map<string, GeckoCircumferentialWeld>())
  }
  public static CreateFromJSON(
    componentID: string,
    weldID: string,
    weldJson: GeckoCircumferentialWeldJSON
  ): GeckoCircumferentialWeld {
    return new GeckoCircumferentialWeld(componentID, weldID, weldJson)
  }

  public asJSON() {
    return generateCircumferentialWeld(this.name, this.feature_geometry.y.min)
  }
}

export function generateCircumferentialWeld(
  name: string | undefined,
  y: GeckoMeasurement
): GeckoCircumferentialWeldJSON {
  const CircumferentialWeldJSON: GeckoCircumferentialWeldJSON = {
    name: name ?? undefined,
    type: GeckoFeatureModelType.CircumferentialWeld,
    feature_geometry: {
      y: y
        .convertTo(GeckoMeasurementUnit.Meters)
        .toPrecision(assetModelPrecision).num,
    },
  }

  return CircumferentialWeldJSON
}

export const CircumferentialWeldFormFields = (
  weldJSON: GeckoCircumferentialWeldJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Weld Name',
      value: weldJSON.name,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'y',
      label: 'Y Position',
      value: weldJSON.feature_geometry.y,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: optionalNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
  ]
}

export const CircumferentialWeldJSONFromFormFields = (
  formFields: FormField[]
) => {
  return generateCircumferentialWeld(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'y')?.value as number,
      GeckoMeasurementUnit.Meters
    )
  )
}

export const CircumferentialWeldFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.CircumferentialWeld,
  title: 'Circumferential Welds',
  singularName: 'Circumferential Weld',
  fields: ['y'],
}
